import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';
import { getPref, setPref, PREF_KEYS } from '../utils/preferences';
import { isChatSoundMuted, setChatSoundMuted } from '../utils/sound';

// How often an OPEN conversation's messages refresh -- near-instant
// without needing a full real-time (WebSocket) layer.
const MESSAGE_POLL_MS = 4000;

// Keep in sync with MAX_CHAT_ATTACHMENTS_BYTES in tickets/views.py.
const MAX_CHAT_ATTACHMENTS_BYTES = 10 * 1024 * 1024;

const initials = (name = '') => name.replace(/^#\s*/, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const nospace = (v) => (v || '').replace(/\s+/g, '');
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const dayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (sameDay(d, now)) return 'Today';
  if (sameDay(d, y)) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const shortTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (sameDay(d, y)) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const IMG_RE = /\.(png|jpe?g|gif|webp)$/i;

// Message text -> links + highlighted @mentions.
const renderBody = (text, knownNames, myKeys) => {
  const parts = String(text).split(/(https?:\/\/[^\s<]+|@[\w.-]+)/g);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const m = part.match(/^(.*?)([.,;:!?)]*)$/);
      return (
        <React.Fragment key={i}>
          <a href={m[1]} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{m[1]}</a>{m[2]}
        </React.Fragment>
      );
    }
    if (part.startsWith('@') && knownNames.has(part.slice(1).toLowerCase())) {
      const me = myKeys.has(part.slice(1).toLowerCase());
      return <span key={i} className={`chat-mention${me ? ' chat-mention-me' : ''}`}>{part}</span>;
    }
    return part;
  });
};

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)}MB` : `${Math.ceil(bytes / 1024)}KB`);

/**
 * Internal chat: 1:1 direct messages and named group chats. Two window
 * modes, either works the same underneath:
 *
 *  - "docked" (default): a small panel pinned to the bottom-right corner,
 *    Messenger-style -- never covers the board behind it, so replying to
 *    a message and working a ticket can happen side by side. Minimizing
 *    collapses it down to just its header bar (still mounted, still
 *    polling, so the unread badge stays live) without losing where you
 *    were; the header itself doubles as that toggle.
 *  - "center": the original full-size modal, centered on screen with the
 *    conversation list always visible alongside the open thread -- for
 *    anyone who preferred that layout. Picked per-browser via a
 *    persisted preference (CHAT_WINDOW_MODE), switchable any time from
 *    either mode's header.
 *
 * Closing (X) unmounts it entirely either way -- background "new
 * message" toasts/tab-blink/sound (see App.js) keep working regardless,
 * since those poll independently of whether this is open.
 *
 * A group's participant list is independent of any Team from the moment
 * it's created -- picking a Team when starting one just pre-fills who to
 * add, it isn't kept in sync afterwards. Any current participant (or any
 * admin) can add someone else at any time, even if they're not on that
 * Team at all.
 *
 * Tagging: type @username anywhere in a message to tag a fellow
 * participant -- they get a bell notification, same as being tagged on a
 * ticket. Matched against the current conversation's own participants.
 */
const ChatModal = ({ employee, employees, teams, onClose, onUnreadChanged, openConversationId, onOpenedConversation }) => {
  const [view, setView] = useState('list'); // 'list' | 'thread' | 'new-dm' | 'new-group'
  const [minimized, setMinimized] = useState(false);
  const [windowMode, setWindowMode] = useState(() => getPref(PREF_KEYS.CHAT_WINDOW_MODE, 'docked')); // 'docked' | 'center'
  const [soundMuted, setSoundMuted] = useState(() => isChatSoundMuted());
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const [convFilter, setConvFilter] = useState('');
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const lastCountRef = useRef(0);
  const [newBelow, setNewBelow] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // @-mention autocomplete
  const [mention, setMention] = useState(null); // { start, query } or null
  const [mentionIdx, setMentionIdx] = useState(0);

  // new-group form
  const [groupName, setGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState(new Set());
  // Customers added to the group being built -- kept separately from
  // groupMemberIds (which is just a Set of ids) so we can still show
  // their name as a chip once they scroll out of the current search
  // results. Keyed by mavis_user_id.
  const [groupSelectedCustomers, setGroupSelectedCustomers] = useState(new Map());
  const [groupCustomerQuery, setGroupCustomerQuery] = useState('');
  const [groupCustomerResults, setGroupCustomerResults] = useState([]);
  const [searchingGroupCustomers, setSearchingGroupCustomers] = useState(false);
  const groupCustomerSearchTimer = useRef(null);

  // Customer search for "New DM" -- staff can message a customer
  // directly, not just a fellow employee, now that chat is shared with
  // mavis_backend's unified system. Debounced so we're not hitting
  // /api/customers/search/ on every keystroke.
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const customerSearchTimer = useRef(null);

  const active = conversations.find((c) => c.id === activeId) || null;

  const refreshList = () => {
    api.getChats()
      .then((data) => {
        setConversations(data);
        onUnreadChanged?.();
      })
      .catch(() => toast.error('Could not load chats.'))
      .finally(() => setLoadingList(false));
  };

  useEffect(refreshList, []);

  // Deep-link from a "new message" toast: jump straight to that thread
  // once the modal's open, then clear the request so re-opening the
  // modal later doesn't jump anywhere unexpected.
  useEffect(() => {
    if (!openConversationId) return;
    setActiveId(openConversationId);
    setView('thread');
    setMinimized(false);
    onOpenedConversation?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversationId]);

  const openThread = (conversationId) => {
    stickRef.current = true;
    lastCountRef.current = 0;
    setNewBelow(false);
    setActiveId(conversationId);
    setView('thread');
  };

  useEffect(() => {
    if (view !== 'thread' || !activeId) return undefined;
    const load = () => {
      api.getChatMessages(activeId)
        .then((data) => { setMessages(data); onUnreadChanged?.(); refreshList(); })
        .catch(() => toast.error('Could not load messages.'));
    };
    load();
    const interval = setInterval(load, MESSAGE_POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeId]);

  const otherParticipants = useMemo(
    () => (active ? active.participants.filter((p) => p.mavis_user_id !== employee.mavis_user_id) : []),
    [active, employee],
  );

  const knownNames = useMemo(() => {
    const set = new Set();
    [...(employees || []), ...(active ? active.participants : [])].forEach((e) => {
      if (e.display_name) set.add(nospace(e.display_name).toLowerCase());
      if (e.username) set.add(String(e.username).toLowerCase());
    });
    return set;
  }, [employees, active]);

  const myKeys = useMemo(() => new Set([
    nospace(employee.display_name).toLowerCase(), String(employee.username || '').toLowerCase(),
  ].filter(Boolean)), [employee]);

  // Who can be tagged: in a group, any colleague (people not yet in the group are
  // added automatically when the message is sent). Chat members are listed first.
  const mentionCandidates = useMemo(() => {
    if (!mention || !active || !active.is_group) return [];
    const memberIds = new Set(active.participants.map((p) => p.mavis_user_id));
    const q = mention.query.toLowerCase();
    const pool = new Map();
    active.participants.forEach((p) => pool.set(p.mavis_user_id, { ...p, inChat: true }));
    (employees || []).forEach((e) => { if (!pool.has(e.mavis_user_id)) pool.set(e.mavis_user_id, { ...e, inChat: memberIds.has(e.mavis_user_id) }); });
    return Array.from(pool.values())
      .filter((p) => p.mavis_user_id !== employee.mavis_user_id)
      .filter((p) => !q || (p.display_name || '').toLowerCase().includes(q) || (p.username || '').toLowerCase().includes(q))
      .sort((a, b) => Number(b.inChat) - Number(a.inChat) || (a.display_name || '').localeCompare(b.display_name || ''))
      .slice(0, 6);
  }, [mention, active, employees, employee]);

  // Keep the newest message in view -- unless the reader has scrolled up to read history.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || view !== 'thread') return;
    const grew = messages.length > lastCountRef.current;
    if (stickRef.current) {
      el.scrollTop = el.scrollHeight;
      setNewBelow(false);
    } else if (grew && lastCountRef.current > 0) {
      setNewBelow(true);
    }
    lastCountRef.current = messages.length;
  }, [messages, view]);

  // Message box grows with the text (up to a limit).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 130)}px`;
  }, [draft, view, activeId]);

  const onThreadScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickRef.current = nearBottom;
    if (nearBottom) setNewBelow(false);
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    stickRef.current = true;
    setNewBelow(false);
  };

  const detectMention = (text, caret) => {
    if (!active || !active.is_group) { setMention(null); return; }
    const m = text.slice(0, caret).match(/(?:^|\s)@([\w.-]*)$/);
    if (m) {
      setMention({ start: caret - m[1].length - 1, query: m[1] });
      setMentionIdx(0);
    } else {
      setMention(null);
    }
  };

  const pickMention = (person) => {
    if (!mention) return;
    const before = draft.slice(0, mention.start);
    const after = draft.slice(mention.start + 1 + mention.query.length);
    const insert = `@${nospace(person.display_name || person.username)} `;
    const next = `${before}${insert}${after}`;
    setDraft(next);
    setMention(null);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { const pos = before.length + insert.length; ta.focus(); ta.setSelectionRange(pos, pos); }
    });
  };

  const totalPendingBytes = pendingFiles.reduce((sum, f) => sum + f.size, 0);
  const pendingTooLarge = totalPendingBytes > MAX_CHAT_ATTACHMENTS_BYTES;

  // Screenshots pasted from the clipboard all arrive as "image.png" -- give them
  // a unique, readable name so several can be told apart.
  const handleFilesChosen = (fileList) => {
    const stamp = new Date().toLocaleTimeString([], { hour12: false }).replace(/:/g, '');
    const incoming = Array.from(fileList).map((f, i) => (
      /^image\.(png|jpe?g|gif|webp)$/i.test(f.name)
        ? new File([f], `screenshot-${stamp}${i ? `-${i + 1}` : ''}.${f.name.split('.').pop().toLowerCase()}`, { type: f.type })
        : f
    ));
    setPendingFiles((prev) => [...prev, ...incoming]);
  };

  // Thumbnails for pending image attachments.
  const pendingPreviews = useMemo(
    () => pendingFiles.map((f) => (f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [pendingFiles],
  );
  useEffect(() => () => pendingPreviews.forEach((u) => { if (u) URL.revokeObjectURL(u); }), [pendingPreviews]);

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if ((!draft.trim() && pendingFiles.length === 0) || !activeId) return;
    if (pendingTooLarge) {
      toast.error(`Attachments are too large (${formatSize(totalPendingBytes)}) -- keep them under ${formatSize(MAX_CHAT_ATTACHMENTS_BYTES)} combined.`);
      return;
    }
    // Find everyone @-mentioned in the text (by name without spaces, or username).
    const mentioned = new Map();
    [...active.participants, ...(active.is_group ? (employees || []) : [])].forEach((p) => {
      if (p.mavis_user_id === employee.mavis_user_id) return;
      const keys = [nospace(p.display_name), p.username].filter(Boolean);
      if (keys.some((k) => new RegExp(`(^|\\s)@${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w.-])`, 'i').test(draft))) {
        mentioned.set(p.mavis_user_id, p);
      }
    });
    const taggedIds = Array.from(mentioned.keys());
    const memberIds = new Set(active.participants.map((p) => p.mavis_user_id));
    const toAdd = taggedIds.filter((id) => !memberIds.has(id));
    try {
      setSending(true);
      if (active.is_group && toAdd.length) {
        await api.addChatParticipants(activeId, toAdd);
        toast.info(`Added ${toAdd.length === 1 ? mentioned.get(toAdd[0]).display_name : `${toAdd.length} people`} to the group.`);
      }
      await api.postChatMessage(activeId, { body: draft.trim(), taggedEmployeeIds: taggedIds, files: pendingFiles });
      setDraft('');
      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      stickRef.current = true;
      const data = await api.getChatMessages(activeId);
      setMessages(data);
      refreshList();
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send that message.');
    } finally {
      setSending(false);
    }
  };

  const handleStartDirect = async (targetEmployee) => {
    try {
      const conv = await api.startDirectChat(targetEmployee.mavis_user_id);
      refreshList();
      openThread(conv.id);
    } catch (error) {
      toast.error('Could not start that chat.');
    }
  };

  const handleCustomerQueryChange = (value) => {
    setCustomerQuery(value);
    if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setCustomerResults([]);
      setSearchingCustomers(false);
      return;
    }
    setSearchingCustomers(true);
    customerSearchTimer.current = setTimeout(async () => {
      try {
        const results = await api.searchCustomers(trimmed);
        setCustomerResults(results);
      } catch (error) {
        toast.error('Could not search customers.');
      } finally {
        setSearchingCustomers(false);
      }
    }, 350);
  };

  const toggleGroupMember = (mavisUserId) => {
    setGroupMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(mavisUserId)) next.delete(mavisUserId); else next.add(mavisUserId);
      return next;
    });
  };

  const applyTeamToGroupSelection = (teamId) => {
    const team = (teams || []).find((t) => String(t.id) === String(teamId));
    if (!team) return;
    setGroupMemberIds((prev) => new Set([...prev, ...team.members.map((m) => m.mavis_user_id)]));
  };

  const toggleGroupCustomer = (customer) => {
    setGroupMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(customer.mavis_user_id)) next.delete(customer.mavis_user_id); else next.add(customer.mavis_user_id);
      return next;
    });
    setGroupSelectedCustomers((prev) => {
      const next = new Map(prev);
      if (next.has(customer.mavis_user_id)) next.delete(customer.mavis_user_id); else next.set(customer.mavis_user_id, customer);
      return next;
    });
  };

  const handleGroupCustomerQueryChange = (value) => {
    setGroupCustomerQuery(value);
    if (groupCustomerSearchTimer.current) clearTimeout(groupCustomerSearchTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setGroupCustomerResults([]);
      setSearchingGroupCustomers(false);
      return;
    }
    setSearchingGroupCustomers(true);
    groupCustomerSearchTimer.current = setTimeout(async () => {
      try {
        const results = await api.searchCustomers(trimmed);
        setGroupCustomerResults(results);
      } catch (error) {
        toast.error('Could not search customers.');
      } finally {
        setSearchingGroupCustomers(false);
      }
    }, 350);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) { toast.error('A group name is required.'); return; }
    if (groupMemberIds.size === 0) { toast.error('Pick at least one other person.'); return; }
    try {
      const conv = await api.createGroupChat(groupName.trim(), Array.from(groupMemberIds));
      toast.success('Group created.');
      setGroupName('');
      setGroupMemberIds(new Set());
      setGroupSelectedCustomers(new Map());
      setGroupCustomerQuery('');
      setGroupCustomerResults([]);
      refreshList();
      openThread(conv.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not create the group.');
    }
  };

  const handleAddParticipant = async (targetEmployee) => {
    if (!activeId) return;
    try {
      await api.addChatParticipants(activeId, [targetEmployee.mavis_user_id]);
      toast.success(`Added ${targetEmployee.display_name}.`);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not add that person.');
    }
  };

  const handleLeave = async () => {
    if (!activeId || !window.confirm('Leave this group?')) return;
    try {
      await api.leaveChat(activeId);
      setView('list');
      setActiveId(null);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not leave the group.');
    }
  };

  const addableEmployees = useMemo(() => {
    if (!active) return [];
    const participantIds = new Set(active.participants.map((p) => p.mavis_user_id));
    return (employees || []).filter((e) => !participantIds.has(e.mavis_user_id));
  }, [active, employees]);

  const handleToggleWindowMode = () => {
    const next = windowMode === 'docked' ? 'center' : 'docked';
    setWindowMode(next);
    setPref(PREF_KEYS.CHAT_WINDOW_MODE, next);
    setMinimized(false);
  };

  const handleToggleSound = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    setChatSoundMuted(next);
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const showBack = view === 'thread' || view === 'new-dm' || view === 'new-group';
  const headerTitle = view === 'thread' && active
    ? (active.is_group ? `# ${active.name}` : active.name)
    : view === 'new-dm' ? 'New DM'
    : view === 'new-group' ? 'New group'
    : 'Chat';

  const soundToggleBtn = (
    <button
      type="button" className="docked-chat-icon-btn"
      aria-label={soundMuted ? 'Unmute message sound' : 'Mute message sound'}
      title={soundMuted ? 'Sound muted -- click to unmute' : 'Sound on -- click to mute'}
      onClick={(e) => { e.stopPropagation(); handleToggleSound(); }}
    >
      {soundMuted ? '🔇' : '🔊'}
    </button>
  );

  const filteredConversations = conversations.filter((c) => {
    const q = convFilter.trim().toLowerCase();
    if (!q) return true;
    return `${c.name} ${c.last_message?.body || ''}`.toLowerCase().includes(q);
  });

  const conversationListContent = (
    <>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' }}>
        <button type="button" className="btn-primary" style={{ flex: 1, fontSize: '0.78rem' }} onClick={() => setView('new-dm')}>+ New message</button>
        <button type="button" className="btn-secondary" style={{ flex: 1, fontSize: '0.78rem' }} onClick={() => setView('new-group')}>+ New group</button>
      </div>
      {conversations.length > 4 && (
        <input
          type="search" className="form-input" style={{ marginBottom: '0.5rem', fontSize: '0.82rem' }}
          placeholder="Search chats..." value={convFilter} onChange={(e) => setConvFilter(e.target.value)} aria-label="Search chats"
        />
      )}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loadingList ? <p style={{ fontSize: '0.85rem' }}>Loading...</p> : conversations.length === 0 ? (
          <p style={{ color: '#aab7c4', fontSize: '0.82rem' }}>No conversations yet. Start one with "+ New message".</p>
        ) : filteredConversations.length === 0 ? (
          <p style={{ color: '#aab7c4', fontSize: '0.82rem' }}>No chats match.</p>
        ) : filteredConversations.map((c) => (
          <div
            key={c.id}
            role="button" tabIndex={0}
            onClick={() => openThread(c.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openThread(c.id); } }}
            className="chat-list-row"
            style={{ background: c.id === activeId ? 'rgba(255,255,255,0.08)' : undefined }}
          >
            <span className={`chat-avatar${c.is_group ? ' chat-avatar-group' : ''}`} aria-hidden="true">{c.is_group ? '#' : initials(c.name)}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <strong style={{ fontSize: '0.85rem', fontWeight: c.unread_count > 0 ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</strong>
                <span style={{ fontSize: '0.68rem', color: c.unread_count > 0 ? 'var(--accent)' : '#aab7c4', flexShrink: 0 }}>{shortTime(c.last_message?.created_at)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: c.unread_count > 0 ? 'inherit' : '#aab7c4', fontWeight: c.unread_count > 0 ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.last_message ? `${c.last_message.sender ? `${c.last_message.sender}: ` : ''}${c.last_message.body || 'Attachment'}` : 'No messages yet'}
                </p>
                {c.unread_count > 0 && <span className="notif-badge" style={{ position: 'static', flexShrink: 0 }}>{c.unread_count > 9 ? '9+' : c.unread_count}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const threadContent = active && (
    <div
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) handleFilesChosen(e.dataTransfer.files); }}
    >
      {dragOver && <div className="chat-drop-overlay">Drop files to attach</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-subtle)' }}>
        <span className={`chat-avatar${active.is_group ? ' chat-avatar-group' : ''}`} aria-hidden="true">{active.is_group ? '#' : initials(active.name)}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong style={{ fontSize: '0.9rem' }}>{active.name}</strong>
          <div style={{ fontSize: '0.72rem', color: '#aab7c4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {active.is_group
              ? `${active.participants.length} members: ${active.participants.map((p) => p.display_name || p.username).join(', ')}`
              : 'Direct message'}
          </div>
        </div>
        {active.is_group && (
          <>
            <select className="form-select" style={{ fontSize: '0.72rem', maxWidth: 120 }} value="" aria-label="Add person" onChange={(e) => {
              const emp = addableEmployees.find((x) => String(x.mavis_user_id) === e.target.value);
              if (emp) handleAddParticipant(emp);
            }}>
              <option value="">+ Add...</option>
              {addableEmployees.map((e) => <option key={e.mavis_user_id} value={e.mavis_user_id}>{e.display_name}</option>)}
            </select>
            <button type="button" className="btn-secondary" style={{ fontSize: '0.72rem' }} onClick={handleLeave}>Leave</button>
          </>
        )}
      </div>

      <div ref={scrollRef} onScroll={onThreadScroll} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.5rem', position: 'relative' }}>
        {messages.map((m, i) => {
          const isMine = m.sender?.mavis_user_id === employee.mavis_user_id;
          const prev = i > 0 ? messages[i - 1] : null;
          const newDay = !prev || !sameDay(new Date(prev.created_at), new Date(m.created_at));
          const prevSenderId = prev && !newDay ? prev.sender?.mavis_user_id : null;
          const showSenderName = active.is_group && !isMine && m.sender?.mavis_user_id !== prevSenderId;
          const tagsMe = !isMine && (m.tagged_employees || []).some((t) => t.mavis_user_id === employee.mavis_user_id);
          return (
            <React.Fragment key={m.id}>
              {newDay && <div className="chat-day-sep"><span>{dayLabel(m.created_at)}</span></div>}
              <div className={`chat-bubble-row ${isMine ? 'chat-bubble-row-mine' : 'chat-bubble-row-theirs'}`}>
                {showSenderName && <span className="chat-bubble-sender">{m.sender?.display_name || 'Unknown'}</span>}
                <div className={`chat-bubble ${isMine ? 'chat-bubble-mine' : 'chat-bubble-theirs'}${tagsMe ? ' chat-bubble-tagged' : ''}`}>
                  {m.body && <p style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{renderBody(m.body, knownNames, myKeys)}</p>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div style={{ marginTop: m.body ? '0.3rem' : 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {m.attachments.map((a) => (IMG_RE.test(a.original_filename || '') ? (
                        <a key={a.id} href={a.file} target="_blank" rel="noreferrer">
                          <img src={a.file} alt={a.original_filename} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, display: 'block' }} />
                        </a>
                      ) : (
                        <a key={a.id} href={a.file} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: 'inherit', textDecoration: 'underline' }}>
                          📎 {a.original_filename}
                        </a>
                      )))}
                    </div>
                  )}
                </div>
                <span className="chat-bubble-time">
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </React.Fragment>
          );
        })}
        {messages.length === 0 && <p style={{ color: '#aab7c4', fontSize: '0.82rem' }}>No messages yet -- say hi.</p>}
      </div>
      {newBelow && (
        <button type="button" className="chat-new-below" onClick={jumpToBottom}>New messages &darr;</button>
      )}
      {pendingFiles.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
          {pendingFiles.map((f, i) => (
            <span key={`${f.name}-${i}`} className="label-chip" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              {pendingPreviews[i]
                ? <img src={pendingPreviews[i]} alt="" style={{ height: 48, maxWidth: 90, objectFit: 'cover', borderRadius: 4 }} />
                : '📎'} {f.name} ({formatSize(f.size)})
              <button
                type="button" onClick={() => removePendingFile(i)} aria-label={`Remove ${f.name}`}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >
                &times;
              </button>
            </span>
          ))}
          {pendingTooLarge && (
            <span style={{ fontSize: '0.72rem', color: '#ff9a9d' }}>
              Over the {formatSize(MAX_CHAT_ATTACHMENTS_BYTES)} limit -- remove something.
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-end', position: 'relative' }}>
        {mention && mentionCandidates.length > 0 && (
          <div className="chat-mention-pop" role="listbox" aria-label="Tag someone">
            {mentionCandidates.map((p, i) => (
              <div
                key={p.mavis_user_id} role="option" aria-selected={i === mentionIdx}
                className={`chat-mention-item${i === mentionIdx ? ' active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pickMention(p); }}
                onMouseEnter={() => setMentionIdx(i)}
              >
                <span className="chat-avatar chat-avatar-sm" aria-hidden="true">{initials(p.display_name || p.username)}</span>
                <span style={{ flex: 1 }}>{p.display_name || p.username}</span>
                {!p.inChat && <span style={{ fontSize: '0.68rem', color: '#aab7c4' }}>will be added</span>}
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="form-textarea" rows={1} style={{ flex: 1, minHeight: 38, maxHeight: 130, resize: 'none', overflowY: 'auto' }}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); detectMention(e.target.value, e.target.selectionStart); }}
          onClick={(e) => detectMention(e.target.value, e.target.selectionStart)}
          onKeyDown={(e) => {
            if (mention && mentionCandidates.length > 0) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => Math.min(i + 1, mentionCandidates.length - 1)); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => Math.max(i - 1, 0)); return; }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentionCandidates[mentionIdx]); return; }
              if (e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
            }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          onPaste={(e) => {
            let files = Array.from(e.clipboardData?.files || []);
            if (!files.length) {
              files = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
            }
            if (files.length) { e.preventDefault(); handleFilesChosen(files); }
          }}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          placeholder={active.is_group ? 'Message... type @ to tag someone (Enter = send, Shift+Enter = new line)' : `Message ${active.name}... (Enter = send, Shift+Enter = new line)`}
          aria-label="Message"
        />
        <input
          type="file" multiple ref={fileInputRef} style={{ display: 'none' }}
          onChange={(e) => { handleFilesChosen(e.target.files); e.target.value = ''; }}
        />
        <button
          type="button" className="btn-secondary" title="Attach a file (or drag & drop / paste)" aria-label="Attach a file"
          onClick={() => fileInputRef.current?.click()}
        >
          📎
        </button>
        <button
          type="button" className="btn-primary" onClick={handleSend}
          disabled={sending || (!draft.trim() && pendingFiles.length === 0)}
        >
          Send
        </button>
      </div>
    </div>
  );

  const newDmContent = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <label htmlFor="chat-customer-search" style={{ fontSize: '0.72rem', color: '#aab7c4', marginBottom: '0.2rem' }}>
        Message a customer
      </label>
      <input
        id="chat-customer-search"
        className="form-input"
        style={{ marginBottom: '0.5rem' }}
        placeholder="Search customers by name or email..."
        value={customerQuery}
        onChange={(e) => handleCustomerQueryChange(e.target.value)}
        aria-describedby="chat-customer-search-results"
      />
      <div id="chat-customer-search-results" aria-live="polite">
        {searchingCustomers && <p style={{ fontSize: '0.78rem', color: '#aab7c4' }}>Searching...</p>}
        {!searchingCustomers && customerQuery.trim().length >= 2 && customerResults.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: '#aab7c4' }}>No customers found.</p>
        )}
        {customerResults.map((cust) => (
          <div
            key={`customer-${cust.mavis_user_id}`}
            role="button" tabIndex={0}
            style={{ padding: '0.4rem 0', cursor: 'pointer', fontSize: '0.85rem' }}
            onClick={() => handleStartDirect(cust)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStartDirect(cust); } }}
          >
            {cust.display_name} <span style={{ color: '#aab7c4', fontSize: '0.72rem' }}>(customer)</span>
          </div>
        ))}
      </div>

      {customerResults.length > 0 && <hr style={{ border: 'none', borderTop: '1px solid var(--border-subtle)', margin: '0.5rem 0' }} />}

      <label style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0.2rem 0' }}>Or message a colleague</label>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {(employees || []).filter((e) => e.mavis_user_id !== employee.mavis_user_id).map((e) => (
          <div key={e.mavis_user_id} style={{ padding: '0.4rem 0', cursor: 'pointer', fontSize: '0.85rem' }} onClick={() => handleStartDirect(e)}>
            {e.display_name}
          </div>
        ))}
      </div>
    </div>
  );

  const newGroupContent = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <input className="form-input" style={{ marginBottom: '0.5rem' }} placeholder="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
      {teams && teams.length > 0 && (
        <select className="form-select" style={{ marginBottom: '0.5rem' }} value="" onChange={(e) => applyTeamToGroupSelection(e.target.value)}>
          <option value="">Pre-fill members from a Team...</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}

      <label htmlFor="chat-group-customer-search" style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0.2rem 0' }}>
        Add customers
      </label>
      <input
        id="chat-group-customer-search"
        className="form-input"
        style={{ marginBottom: '0.4rem' }}
        placeholder="Search customers by name or email..."
        value={groupCustomerQuery}
        onChange={(e) => handleGroupCustomerQueryChange(e.target.value)}
        aria-describedby="chat-group-customer-search-results"
      />
      <div id="chat-group-customer-search-results" aria-live="polite" style={{ marginBottom: '0.4rem' }}>
        {searchingGroupCustomers && <p style={{ fontSize: '0.78rem', color: '#aab7c4' }}>Searching...</p>}
        {!searchingGroupCustomers && groupCustomerQuery.trim().length >= 2 && groupCustomerResults.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: '#aab7c4' }}>No customers found.</p>
        )}
        {groupCustomerResults.map((cust) => (
          <label
            key={`group-customer-${cust.mavis_user_id}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.2rem 0', fontSize: '0.85rem', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={groupMemberIds.has(cust.mavis_user_id)}
              onChange={() => toggleGroupCustomer(cust)}
            />
            {cust.display_name} <span style={{ color: '#aab7c4', fontSize: '0.72rem' }}>(customer)</span>
          </label>
        ))}
      </div>

      {groupSelectedCustomers.size > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
          {Array.from(groupSelectedCustomers.values()).map((cust) => (
            <span key={`selected-customer-${cust.mavis_user_id}`} className="label-chip" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              {cust.display_name} (customer)
              <button
                type="button" onClick={() => toggleGroupCustomer(cust)} aria-label={`Remove ${cust.display_name}`}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0.4rem 0 0.3rem' }}>
        Or pick colleagues -- not limited to a Team's official members.
      </p>
      <div style={{ overflowY: 'auto', flex: 1, marginBottom: '0.5rem' }}>
        {(employees || []).filter((e) => e.mavis_user_id !== employee.mavis_user_id).map((e) => (
          <label key={e.mavis_user_id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.2rem 0', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={groupMemberIds.has(e.mavis_user_id)} onChange={() => toggleGroupMember(e.mavis_user_id)} />
            {e.display_name}
          </label>
        ))}
      </div>
      <button type="button" className="btn-primary" onClick={handleCreateGroup}>Create group</button>
    </div>
  );

  if (windowMode === 'center') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 820, height: '80vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <img src="/logo-mark.png" alt="" aria-hidden="true" style={{ width: '1.2rem', height: '1.2rem' }} />
              Chat
            </h2>
            <span style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              {soundToggleBtn}
              <button type="button" className="docked-chat-icon-btn" title="Move to corner" aria-label="Move to corner" onClick={handleToggleWindowMode}>
                Corner
              </button>
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1rem', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--border-subtle)', paddingRight: '0.75rem' }}>
              {conversationListContent}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {view === 'thread' && threadContent}
              {view === 'new-dm' && newDmContent}
              {view === 'new-group' && newGroupContent}
              {view === 'list' && <p style={{ color: '#aab7c4' }}>Pick a conversation, or start a new one.</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="docked-chat">
      <div
        className="docked-chat-header"
        onClick={() => setMinimized((m) => !m)}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMinimized((m) => !m); } }}
      >
        {showBack && (
          <button
            type="button" className="docked-chat-icon-btn" aria-label="Back to conversations"
            onClick={(e) => { e.stopPropagation(); setView('list'); }}
          >
            &larr;
          </button>
        )}
        <span className="docked-chat-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <img src="/logo-mark.png" alt="" aria-hidden="true" style={{ width: '1rem', height: '1rem' }} />
          {headerTitle}
          {totalUnread > 0 && <span className="notif-badge" style={{ position: 'static', marginLeft: '0.4rem' }}>{totalUnread > 9 ? '9+' : totalUnread}</span>}
        </span>
        <span className="docked-chat-header-actions">
          {soundToggleBtn}
          <button
            type="button" className="docked-chat-icon-btn" title="Open in center window" aria-label="Open in center window"
            onClick={(e) => { e.stopPropagation(); handleToggleWindowMode(); }}
          >
            Center
          </button>
          <button
            type="button" className="docked-chat-icon-btn" aria-label={minimized ? 'Expand chat' : 'Minimize chat'}
            onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}
          >
            {minimized ? '▲' : '▼'}
          </button>
          <button type="button" className="docked-chat-icon-btn" aria-label="Close chat" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            &times;
          </button>
        </span>
      </div>

      {!minimized && (
        <div className="docked-chat-body">
          {view === 'list' && conversationListContent}
          {view === 'thread' && threadContent}
          {view === 'new-dm' && newDmContent}
          {view === 'new-group' && newGroupContent}
        </div>
      )}
    </div>
  );
};

export default ChatModal;
